// ── Number / date formatting ────────────────────────────────────────────────
// Everything the UI renders as a figure goes through here so alignment,
// currency symbol and rounding stay consistent across every page.

const GBP = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const GBP0 = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/** £1,234.56 */
export const money = (v) => GBP.format(num(v));

/** £1,235 - the default for dense financial tables */
export const money0 = (v) => GBP0.format(num(v));

/** £1.2k / £3.4m - for chart labels and tight spaces */
export const moneyCompact = (v) => {
  const n = num(v);
  const abs = Math.abs(n);
  const sign = n < 0 ? "−" : "";
  if (abs >= 1_000_000) return `${sign}£${(abs / 1_000_000).toFixed(1)}m`;
  if (abs >= 1_000) return `${sign}£${(abs / 1_000).toFixed(abs >= 100_000 ? 0 : 1)}k`;
  return `${sign}£${abs.toFixed(0)}`;
};

/** Accounting style: em-dash for zero, minus sign for negatives. */
export const acct = (v) => {
  const n = num(v);
  if (n === 0) return "—";
  return (n < 0 ? "−" : "") + GBP0.format(Math.abs(n));
};

/** 7.5 -> "7.5", 8 -> "8" */
export const hrs = (v) => {
  const n = num(v);
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(1);
};

/** Always two decimals, for hour totals that must foot exactly. */
export const hrs2 = (v) => num(v).toFixed(2);

export const pct = (part, total, dp = 1) =>
  num(total) === 0 ? "—" : `${((num(part) / num(total)) * 100).toFixed(dp)}%`;

export const pct0 = (v, dp = 0) => `${num(v).toFixed(dp)}%`;

export const MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
export const MONTH_LONG  = ["January","February","March","April","May","June",
                            "July","August","September","October","November","December"];

/**
 * Month name for a period number. Period 13 is the year-end adjustment and has
 * no calendar month, so it reads as "Adj" rather than falling back to the raw
 * "13/2025" (or, worse, an undefined month name).
 */
export const periodName = (mm) => {
  const i = parseInt(mm, 10);
  return MONTH_SHORT[i - 1] || (i === 13 ? "Adj" : null);
};

/** "06/2025" -> "Jun 2025"; "13/2025" -> "Adj 2025" */
export const fmtPeriod = (period) => {
  if (!period) return "";
  const [m, y] = String(period).split("/");
  const name = periodName(m);
  return name ? `${name} ${y}` : period;
};

/** "06/2025" -> "Jun 25"; "13/2025" -> "Adj 25" */
export const fmtPeriodShort = (period) => {
  if (!period) return "";
  const [m, y] = String(period).split("/");
  const name = periodName(m);
  return name ? `${name} ${String(y).slice(2)}` : period;
};

export const fmtDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

export const daysSince = (value) => {
  const start = new Date(value);
  if (Number.isNaN(start.getTime())) return 0;
  return Math.floor((Date.now() - start.getTime()) / 86_400_000);
};

/** "AM" -> "AM"; "hutchinson" -> "HU" */
export const initials = (name) => (name || "?").slice(0, 2).toUpperCase();

/** Surname from Deltek's "Surname, Forename" employee strings. */
export const surname = (name) => String(name || "").split(",")[0].trim();

export const truncate = (s, n) =>
  !s ? "" : s.length > n ? `${s.slice(0, n)}…` : s;
